import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  OnInit,
} from "@angular/core";
import Funcionalidades from "@shared/enums/funcionalidades.enum";
import { SessionService } from "@services/session.service";
import ISidebar from "@models/sidebar.interface";
import { environment } from "@env/environment";
import { Router, NavigationEnd } from "@angular/router";
import { Subscription } from "rxjs";

const DISABLE_AUTH = !!(environment as any).disableAuth;

const LABELS: Record<number, { label: string; alt: string }> = {
  [Funcionalidades.registrar_funcionario]: {
    label: "Registrar Funcionario",
    alt: "Registrar funcionario",
  },
  [Funcionalidades.menu_instituciones]: {
    label: "Instituciones",
    alt: "Instituciones",
  }
};

@Component({
  selector: "app-sidebar",
  templateUrl: "./sidebar.component.html",
  styleUrls: ["./sidebar.component.scss"],
})
export class SidebarComponent implements OnChanges, OnInit, OnDestroy {
  @Input() isSidebarOpen = false;
  @Output() toggle = new EventEmitter<void>();

  private documentClickListener?: () => void;
  private routerSub?: Subscription;
  /** almacena rutas expandidas (set de rutas padre) */
  private expanded = new Set<string>();

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private sessionService: SessionService,
    private router: Router
  ) {}

  /** Devuelve textos para una funcionalidad, con fallback en DEV */
  private getText(fn: Funcionalidades) {
    if (DISABLE_AUTH) {
      return LABELS[fn] ?? { label: `Func ${fn}`, alt: "Disponible en DEV" };
    }
    const f = this.sessionService.getFunctionality(fn);
    return {
      label: f?.nombre_fnc ?? LABELS[fn]?.label ?? `Func ${fn}`,
      alt: f?.ayuda_fnc ?? LABELS[fn]?.alt ?? "",
    };
  }

  /** Mostrar SIEMPRE todos los ítems (sin filtrar por permisos)
   *  Ahora permite children para jerarquía.
   */
  get sidebarItems(): ISidebar[] {
    const t = (fn: Funcionalidades) => this.getText(fn);
    const isEvaluado = this.sessionService.isFuncionarioEvaluado();

    return [
      {
        ruta: "/administracion",
        icono: "bi-gear-fill",
        label: "Administración",
        alt: "Administración",
        visible: !isEvaluado,
        children: [
          {
            ruta: '/administracion/reportes',
            icono: 'bi-bar-chart',
            label: 'Reportes y Analíticas',
            alt: 'Reportes y analíticas',
            visible: !isEvaluado,
          },
      
        ],
      },
      {
        ruta: '/administracion/dictamen',
        icono: 'bi-list-check',
        label: 'Listado de casos',
        alt: 'Listado de casos',
        visible: true,
      },
      {
        ruta: '/solicitud-salud-irrecuperable',
        icono: 'bi-file-earmark-check',
        label: 'Solicitud Salud Irrecuperable',
        alt: 'Solicitar evaluación de salud irrecuperable',
        visible: !isEvaluado,
      },
    ].filter(item => item.visible);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["isSidebarOpen"]) {
      if (this.isSidebarOpen) setTimeout(() => this.addDocumentClickListener());
      else this.removeDocumentClickListener();
    }
  }

  /** Cerrar solo en móvil tras navegar */
  onNavClick(): void {
    if (window.innerWidth < 576 && this.isSidebarOpen) {
      setTimeout(() => this.toggle.emit(), 0);
    }
  }

  /** Cerrar al click fuera */
  private addDocumentClickListener(): void {
    this.documentClickListener = this.renderer.listen(
      "document",
      "click",
      (event: MouseEvent) => {
        if (!this.elementRef.nativeElement.contains(event.target)) {
          this.toggle.emit();
        }
      }
    );
  }

  private removeDocumentClickListener(): void {
    if (this.documentClickListener) {
      this.documentClickListener();
      this.documentClickListener = undefined;
    }
  }

  trackByRuta(index: number, item: ISidebar): string {
    return item.ruta;
  }

  closeSidebar(event?: Event): void {
    if (this.isSidebarOpen || event) this.toggle.emit();
  }

  ngOnInit(): void {
    // expandir padres según la ruta activa
    this.routerSub = this.router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.syncExpandedWithRoute(e.urlAfterRedirects);
      }
    });
    // inicial sync
    this.syncExpandedWithRoute(this.router.url);
  }

  ngOnDestroy(): void {
    this.removeDocumentClickListener();
    this.routerSub?.unsubscribe();
  }

  /** Gestion de expansión de submenús */
  isExpanded(item: ISidebar): boolean {
    return this.expanded.has(item.ruta);
  }

  toggleExpand(item: ISidebar, event?: Event): void {
    if (event) event.stopPropagation();
    if (!item.children || item.children.length === 0) return;
    if (this.expanded.has(item.ruta)) this.expanded.delete(item.ruta);
    else this.expanded.add(item.ruta);
  }

  /** Sincroniza el set de expandido en función de la ruta activa */
  private syncExpandedWithRoute(currentUrl: string): void {
    const path = this.findPathToRoute(this.sidebarItems, currentUrl);
    this.expanded.clear();
    // expandir todos los nodos padres (todo menos el último que es la ruta activa)
    if (path && path.length > 1) {
      path.slice(0, -1).forEach((r) => this.expanded.add(r));
    }
  }

  /** Busca recursivamente la cadena de rutas desde root hasta la ruta que coincida con la URL.
   *  Devuelve array de rutas (ej: ['/expedientes', '/expedientes/crear']) o undefined.
   */
  private findPathToRoute(
    items: ISidebar[],
    targetUrl: string
  ): string[] | undefined {
    const normalized = (u?: string) =>
      u ? (u.endsWith("/") ? u.slice(0, -1) : u) : u;
    const t = normalized(targetUrl);
    for (const it of items) {
      const r = normalized(it.ruta);
      // si item.ruta es prefijo de la url, lo consideramos candidato
      if (r && (t === r || t?.startsWith(r + "/"))) {
        // si no tiene children, devolvemos este como coincidencia
        if (!it.children || it.children.length === 0) return [it.ruta];
        // buscar en hijos
        const childPath = this.findPathToRoute(it.children, targetUrl);
        if (childPath) return [it.ruta, ...childPath];
        // si ningún hijo coincidió, consideramos el padre como coincidencia
        return [it.ruta];
      }
      // si no coincide directamente, buscar en hijos igualmente
      if (it.children && it.children.length) {
        const childPath = this.findPathToRoute(it.children, targetUrl);
        if (childPath) return [it.ruta, ...childPath];
      }
    }
    return undefined;
  }
}
